import React, { Suspense } from 'react'
import { Params, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { LoginPage } from './pages/login'
import { useAppContext, User } from './providers/context'
import { useAppInit } from './hooks/init'
import { HomePage } from './pages/home/home'
import { NotFoundPage } from './pages/notFound'
import { MessagePreviewOverlay } from './pages/modals/preview'
import { MessageInfoOverlay } from './pages/modals/messageinfo/messageInfo'
import { FamilyContactsPage } from './pages/familyContacts/familyContacts'
import { AppModals } from './pages/modals/appModals'
import { TeacherContactsPage } from './pages/teacherContacts/teacherContacts'
import { AccountSwitcherPage } from './pages/accountSwitcher'
import { ContactsReportPage } from './pages/contactsReport'
import { DistrictStatsPage } from './pages/districtStats'
import { YourStatsPage } from './pages/yourStats'
import { AttendanceStatsPage } from './pages/attendanceStats'
import { DashboardPage } from './pages/dashboard/dashboard'
import { PushNotificationSetupPage } from './pages/pushNotificationSetup'
import { PTCConferencePage } from './pages/ptc/conferenceDashboard'
import { PTCEditConferencePage } from './pages/ptc/editConference'
import { PTCFamilyHomePage } from './pages/ptc/familyHome'
import { PTCFamilySchedulerPage } from './pages/ptc/familyScheduler'
import { PTCNewConferencePage } from './pages/ptc/newConference'
import { ExclusionClassesPage } from './pages/ptc/exclusionClasses'
import { VerifyInvitesPage } from './pages/ptc/verifyInvites'
import { TeacherInfoPage } from './pages/ptc/teacherInfo'
import { DistrictOnboardingPage } from './pages/onboarding/districtOnboarding'
import { SchoolOnboardingPage } from './pages/onboarding/schoolOnboarding'
import { AppRoute, InitialUserRedirect, LoadingOverlay, Logout, Redirect } from './routerComponents'
import { DeliveryStatusPage } from './pages/deliveryStatus'
import { CreateAssessment, EditAssessment } from './pages/assessments/createOrEdit'
import { CreateSurvey } from './pages/survey/createSurvey'
import { AssessmentDashboard } from './pages/assessments/assessmentDashboard'
import { StudentAttendanceStatsPage } from './pages/studentAttendanceStats'

const LazyEditSurvey = React.lazy(() => import('./pages/survey/editSurvey'))
const LazySurveyDashboard = React.lazy(() => import('./pages/survey/surveyDashboard'))
const LazyFillSurvey = React.lazy(() => import('./pages/survey/fillSurvey'))

const APP_ROUTES: AppRoute[] = [
  {
    path: '/beta',
    element: (user) => <Redirect to={'/'} />,
  },
  {
    path: '/',
    element: (user) => (user ? <HomePage user={user} /> : <LoginPage />),
  },
  {
    path: '/logout',
    element: () => <Logout />,
  },
  {
    path: '/contacts/families',
    element: (user) =>
      user?.type === 'teacher' && !user?.externalStaff ? <FamilyContactsPage user={user} /> : <Redirect to="/" />,
  },
  {
    path: '/contacts/teachers',
    element: (user) =>
      user?.type === 'teacher' && user?.admin > 0 && !user?.externalStaff ? (
        <TeacherContactsPage user={user} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/contacts/report',
    element: (user) =>
      user?.type === 'teacher' && user?.admin > 0 && !user?.externalStaff ? (
        <ContactsReportPage user={user} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/dashboard',
    element: (user) =>
      user?.type === 'teacher' ? (
        <Redirect
          to={
            user.districtAccess || !user.schools.length
              ? '/dashboard/district'
              : '/dashboard/school/' + user.schools[0].id
          }
        />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/dashboard/district',
    element: (user) =>
      user?.type === 'teacher' && user?.admin > 0 && !user?.externalStaff ? (
        <DashboardPage user={user} schoolID={undefined} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/dashboard/school/:schoolID',
    element: (user, params) =>
      user?.type === 'teacher' && user?.admin > 0 && !user?.externalStaff ? (
        <DashboardPage user={user} schoolID={parseInt(params!.schoolID!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/stats/district',
    element: (user) =>
      user?.type === 'teacher' && user?.admin > 0 && !user?.externalStaff ? (
        <DistrictStatsPage user={user} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/stats/yours',
    element: (user) =>
      user?.type === 'teacher' && !user?.externalStaff ? <YourStatsPage user={user} /> : <Redirect to="/" />,
  },
  {
    path: '/stats/attendance',
    element: (user) =>
      user?.type === 'teacher' && !user?.externalStaff ? <AttendanceStatsPage user={user} /> : <Redirect to="/" />,
  },
  {
    path: '/stats/attendance/student/:studentID',
    element: (user) =>
      user?.type === 'teacher' && !user?.externalStaff ? (
        <StudentAttendanceStatsPage user={user} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/pushNotificationSetup',
    element: (user) => (user ? <PushNotificationSetupPage user={user} /> : <Redirect to="/" />),
  },
  {
    path: '/ptc/allInvites/:studentID?',
    element: (user, params) =>
      user?.type === 'guardian' && user.district.ptcEnabled ? (
        <PTCFamilyHomePage user={user} initialStudentID={params?.studentID ? parseInt(params!.studentID) : undefined} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/ptc/inviteResponse/:id',
    element: (user, params) =>
      user?.type === 'guardian' && user.district.ptcEnabled ? (
        <PTCFamilySchedulerPage user={user} appointmentID={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/ptc/conference/:id',
    element: (user, params) =>
      user?.type === 'teacher' && user.district.ptcEnabled ? (
        <PTCConferencePage user={user} id={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/ptc/adminConferenceVerification/exclusionClasses/:id',
    element: (user, params) =>
      user?.type === 'teacher' && user.PTCAdmin && user.district.ptcEnabled ? (
        <ExclusionClassesPage user={user} id={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/ptc/adminConferenceVerification/verifyInvites/:id',
    element: (user, params) =>
      user?.type === 'teacher' && user.PTCAdmin && user.district.ptcEnabled ? (
        <VerifyInvitesPage user={user} id={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/ptc/adminConferenceVerification/teacherInfo/:id',
    element: (user, params) =>
      user?.type === 'teacher' && user.PTCAdmin && user.district.ptcEnabled ? (
        <TeacherInfoPage user={user} id={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/ptc/newConference',
    element: (user) =>
      user?.type === 'teacher' && user.PTCAdmin && user.district.ptcEnabled ? (
        <PTCNewConferencePage user={user} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/ptc/editConference/:id',
    element: (user, params) =>
      user?.type === 'teacher' && user.PTCAdmin && user.district.ptcEnabled ? (
        <PTCEditConferencePage user={user} id={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/onboarding/district',
    element: (user?: User) =>
      user?.type === 'teacher' && user.admin === 2 && user.districtAccess ? (
        <DistrictOnboardingPage user={user} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/onboarding/school',
    element: (user?: User) =>
      user?.type === 'teacher' && user.admin === 2 && !user.districtAccess && user.schools.length === 1 ? (
        <SchoolOnboardingPage user={user} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/admin/deliveryStatus/:id/:incoming?',
    element: (user, params) =>
      user?.type == 'teacher' && user.systemAdministrator ? (
        <DeliveryStatusPage user={user} id={parseInt(params!.id!)} incoming={params!.incoming} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/form/create',
    element: (user?: User) =>
      user?.type === 'teacher' && !user?.externalStaff ? <CreateSurvey user={user} /> : <Redirect to="/" />,
  },
  {
    path: '/form/edit/:id',
    element: (user?: User) =>
      user?.type === 'teacher' && !user?.externalStaff ? <LazyEditSurvey user={user} /> : <Redirect to="/" />,
  },
  {
    path: '/form/dashboard/:id',
    element: (user?: User) =>
      user?.type === 'teacher' && !user?.externalStaff ? <LazySurveyDashboard user={user} /> : <Redirect to="/" />,
  },
  {
    path: '/form/fill/:id/:survey_code?',
    element: (user?: User) => (<LazyFillSurvey user={user} /> ),
  },
  {
    path: '/assessment/create',
    element: (user?: User) =>
      user?.type === 'teacher' && user.showAssessments ? <CreateAssessment user={user} /> : <Redirect to="/" />,
  },
  {
    path: '/assessment/edit/:id',
    element: (user?: User, params?: Params) =>
      user?.type === 'teacher' && user.showAssessments ? (
        <EditAssessment user={user} id={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    path: '/assessment/view/:id',
    element: (user?: User, params?: Params) =>
      user?.showAssessments && user.type === 'teacher' ? (
        <AssessmentDashboard user={user} id={parseInt(params!.id!)} />
      ) : (
        <Redirect to="/" />
      ),
  },
  {
    // Make sure this is always the last route in the list
    // TEMPORARY fix for ptc login links
    path: '/ptc/*',
    element: () => {
      const RedirectOnMount: React.FC = () => {
        React.useEffect(() => {
          window.location.pathname = window.location.pathname.replace('/ptc', '/ptcL')
        }, [])
        return null
      }
      return <RedirectOnMount />
    },
  },
  {
    // Make sure this is always the last route in the list
    // TEMPORARY fix for survey login links
    path: '/form/*',
    element: () => {
      const RedirectOnMount: React.FC = () => {
        React.useEffect(() => {
          window.location.pathname = window.location.pathname.replace('/form', '/formL')
        }, [])
        return null
      }
      return <RedirectOnMount />
    },
  },
  {
    path: '*',
    element: () => <NotFoundPage />,
  },
]

/**
 * Hook to provide smart navigation functionality.
 * Decides between client-side navigation and external redirection.
 * @returns A function that navigates to a given path or URL, automatically determining the type of navigation needed.
 * If the path is an app route, it uses client-side navigation; otherwise, it uses external redirection.
 */
export const useSmartNavigate = () => {
  const navigate = useNavigate()

  return (to: string) => {
    const isAppRoute = APP_ROUTES.some((route) => {
      if (route.path === '*') return false // Skip the 404 route
      const routePath = route.path
        // Handle optional params first => /:paramName? => (?:/[^/]+)?
        .replace(/\/:([a-zA-Z0-9_]+)\?/g, '(?:/[^/]+)?')
        // Then handle normal params => :paramName => [^/]+
        .replace(/:[a-zA-Z0-9_]+/g, '[^/]+')
      const regex = new RegExp(`^${routePath}/?$`)
      try {
        const url = new URL(to, window.location.origin)
        return url.origin === window.location.origin && regex.test(url.pathname)
      } catch (e) {
        // If the URL constructor fails, try directly with the path
        // it may fail if the path is relative
        return regex.test(to.split('?')[0])
      }
    })

    if (isAppRoute) {
      navigate(to) // Client-side navigation
    } else {
      window.location.href = to // External navigation
    }
  }
}

const ParamUnpacker: React.FC<{ children: (params?: Params) => React.ReactNode }> = ({ children }) => {
  const params = useParams()
  return children(params)
}

export const PageRouter: React.FC = () => {
  const { state, showLoadingOverlay, shouldUnmountPage, showLoaderInstantly } = useAppContext()
  useAppInit()

  return (
    <>
      <InitialUserRedirect />
      <LoadingOverlay loading={showLoadingOverlay} openInstantly={shouldUnmountPage || showLoaderInstantly} />
      {state.previewRequest && <MessagePreviewOverlay previewRequest={state.previewRequest} />}
      {state.messageInfoRequest && <MessageInfoOverlay messageInfoRequest={state.messageInfoRequest} />}
      {!shouldUnmountPage && (
        <Suspense fallback={<LoadingOverlay loading openInstantly />}>
          <AppModals />
          {state.user?.showAccountSwitcher ? (
            <AccountSwitcherPage user={state.user} />
          ) : (
            <Routes>
              {APP_ROUTES.map(({ path, element }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    path.includes(':') ? (
                      <ParamUnpacker>{(params) => element(state.user, params)}</ParamUnpacker>
                    ) : (
                      element(state.user)
                    )
                  }
                />
              ))}
            </Routes>
          )}
        </Suspense>
      )}
    </>
  )
}
